import { createSelector } from '@reduxjs/toolkit';
import { UserRoleType, DecodedSsoToken, selectDecodedSsoToken } from '@ovpn-ui-toolkit/mf-common';

export const selectUserRoles = createSelector(
    selectDecodedSsoToken,
    (jwt: DecodedSsoToken) => jwt?.authorities ?? [],
);

export const selectIsMember = createSelector(
    selectUserRoles,
    roles => roles.includes(UserRoleType.Member) && !roles.includes(UserRoleType.Admin),
);

export const selectIsAdmin = createSelector(selectUserRoles, roles =>
    roles.includes(UserRoleType.Admin),
);

/**
 * Returns true for enrolled owner user role
 */
export const selectIsOwner = createSelector(selectUserRoles, roles =>
    roles.includes(UserRoleType.Owner),
);

/**
 * Returns true if owner is not enrolled
 */
export const selectIsPendingOwner = createSelector(
    selectUserRoles,
    roles => roles.includes(UserRoleType.PendingOwner) && !roles.includes(UserRoleType.Owner),
);

/**
 * Returns true if user is enrolled owner or admin
 */
export const selectIsOwnerOrAdmin = createSelector(
    selectIsAdmin,
    selectIsOwner,
    (isAdmin, isOwner) => isAdmin || isOwner,
);

/**
 * Returns true for both Enrolled and non-Enrolled owner role
 */
export const selectIsOwnerAccount = createSelector(
    selectUserRoles,
    roles => roles.includes(UserRoleType.PendingOwner) || roles.includes(UserRoleType.Owner),
);

/**
 * Returns user-friendly string representation of current role
 */
export const selectFrieldlyRole = createSelector(selectUserRoles, roles => {
    if (!Array.isArray(roles)) {
        return '';
    }

    const isEnrolledOwner = roles.includes(UserRoleType.Owner);
    const isPendingOwner = roles.includes(UserRoleType.PendingOwner);
    const isAdmin = roles.includes(UserRoleType.Admin);

    let role = 'Member';

    if (isAdmin) {
        role = 'Admin';
    } else if (isEnrolledOwner || isPendingOwner) {
        role = 'Owner';
    }

    return role;
});
